import Languages from "@data/languages.json";
import { type EligibleAffiliate } from "@interfaces/LandingPricing";
import type Language from "@interfaces/Language";
import { getImageUrl } from "@utilities/base-url";
import { getEligibleAffiliates } from "@utilities/pricing-data";

const eligibleAffiliates: EligibleAffiliate[] = getEligibleAffiliates();

const prefixSet = new Set(
  eligibleAffiliates
    .map(a => a.url_prefix)
    .filter((p): p is string => !!p)
    .map(p => p.toLowerCase()),
);

export const languageIds = new Set((Languages as Language[]).map(l => l.id));

export const isAffiliatePrefix = (segment: string): boolean =>
  prefixSet.has(segment.toLowerCase());

export const findAffiliateByPrefix = (
  segment: string,
): EligibleAffiliate | null => {
  const target = segment.toLowerCase();
  return (
    eligibleAffiliates.find(a => a.url_prefix?.toLowerCase() === target) ?? null
  );
};

export const findAffiliateByCode = (code: string): EligibleAffiliate | null => {
  if (!code) return null;
  const target = code.toLowerCase();
  return eligibleAffiliates.find(a => a.code?.toLowerCase() === target) ?? null;
};

export const getAffiliateLogoUrl = (affiliate: EligibleAffiliate): string =>
  affiliate.logo
    ? getImageUrl("assets/img/affiliates_logos/" + affiliate.logo)
    : "";

// Find the affiliate segment anywhere in the path. Canonical URLs are
// language-first (/es/christian/...) but we also accept the affiliate-first
// form that direct links or older bookmarks may produce.
export const parseAffiliateFromPath = (
  pathname: string,
): EligibleAffiliate | null => {
  const segments = pathname.replace(/^\/+/, "").split("/").filter(Boolean);
  const hit = segments.find(s => isAffiliatePrefix(s));
  return hit !== undefined ? findAffiliateByPrefix(hit) : null;
};

// Remove the affiliate segment from a path, keeping a leading language
// segment and a trailing slash where present.
export const stripAffiliatePrefix = (pathname: string): string => {
  const hadTrailing = pathname.endsWith("/");
  const segments = pathname.replace(/^\/+/, "").split("/").filter(Boolean);
  const idx = segments.findIndex(s => isAffiliatePrefix(s));
  if (idx < 0) return pathname;
  segments.splice(idx, 1);
  const joined = segments.join("/");
  const trailing = joined !== "" && hadTrailing ? "/" : "";
  return "/" + joined + trailing;
};

// Canonicalize a path so the affiliate segment sits RIGHT AFTER the optional
// language segment (language-first, affiliate-second). Examples with prefix
// "christian":
//   /plans/ae               → /christian/plans/ae
//   /es/plans/ae            → /es/christian/plans/ae
//   /es/christian/plans/ae  → unchanged (already canonical)
//   /christian/es/plans/ae  → /es/christian/plans/ae (reorder)
//   /alertify/plans/ae      → unchanged (different affiliate — leave alone)
export const injectAffiliatePrefix = (
  pathname: string,
  prefix: string,
): string => {
  const hadTrailing = pathname.endsWith("/");
  const segments = pathname.replace(/^\/+/, "").split("/").filter(Boolean);
  const existingIdx = segments.findIndex(s => isAffiliatePrefix(s));
  if (
    existingIdx >= 0 &&
    segments[existingIdx].toLowerCase() !== prefix.toLowerCase()
  ) {
    return pathname;
  }
  if (existingIdx >= 0) {
    segments.splice(existingIdx, 1);
  }
  const insertAt = segments.length > 0 && languageIds.has(segments[0]) ? 1 : 0;
  segments.splice(insertAt, 0, prefix);
  const joined = segments.join("/");
  const trailing = joined !== "" && hadTrailing ? "/" : "";
  return "/" + joined + trailing;
};
